import { useState, useEffect } from 'react';
import { instrumentAPI } from '../../../../services';
import '../sections-common.css';
import './InstrumentManager.css';
import InstrumentList from './components/InstrumentList';
import AddInstrument from './components/AddInstrument';
import PresetManager from './components/PresetManager';
import BulkPresetModal from './components/BulkPresetModal';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';

function InstrumentManager() {
  const [instruments, setInstruments] = useState([]);
  const [marketStatus, setMarketStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [dataFetched, setDataFetched] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showPresetManager, setShowPresetManager] = useState(false);
  const [showBulkPresetModal, setShowBulkPresetModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [restoreProgress, setRestoreProgress] = useState({
    isLoading: false,
    current: 0,
    total: 0,
    message: ''
  });
  
  // Restore selection modal state
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [backupInstruments, setBackupInstruments] = useState([]);
  const [selectedRestoreInstruments, setSelectedRestoreInstruments] = useState([]);
  const [restoreSearchTerm, setRestoreSearchTerm] = useState('');
  const [backupFileName, setBackupFileName] = useState('');
  
  // Feature flags - disable market status until backend is ready
  const ENABLE_MARKET_STATUS = true; // Set to true when backend endpoints are available
  const ENABLE_TRADING_HOURS_PRESETS = true; // Set to true when preset endpoints are available
  
  const categories = ['ALL', 'FOREX', 'CRYPTO', 'STOCKS'];

  const validateSpreadPipettes = (value) => {
    const numberValue = Number(value);
    return numberValue >= 0 && numberValue % 2 === 0;
  };

  // Fetch instruments data
  const fetchInstruments = async () => {
    console.log('Fetching instruments data...');
    setLoading(true);
    setError(null);

    try {
      const response = await instrumentAPI.getAll();
      console.log('Successfully fetched instruments:', response.data);
      
      // Ensure we always have an array
      let instrumentsData = response.data;
      if (!Array.isArray(instrumentsData)) {
        // Handle case where API returns { instruments: [...] } or similar
        if (instrumentsData && Array.isArray(instrumentsData.instruments)) {
          instrumentsData = instrumentsData.instruments;
        } else if (instrumentsData && Array.isArray(instrumentsData.data)) {
          instrumentsData = instrumentsData.data;
        } else {
          console.warn('API response is not an array:', instrumentsData);
          instrumentsData = [];
        }
      }
      
      setInstruments(instrumentsData);
      setDataFetched(true);
    } catch (err) {
      console.error('Error in fetchInstruments:', err);
      setError(err.message || 'Failed to fetch instruments');
      setInstruments([]); // Ensure it's always an array even on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch market status for all instruments
  const fetchAllMarketStatus = async () => {
    // Skip if feature is disabled
    if (!ENABLE_MARKET_STATUS) {
      console.log('Market status feature is disabled - skipping API call');
      setMarketStatus({});
      return;
    }

    console.log('Fetching market status for all instruments...');
    setStatusLoading(true);

    try {
      const response = await instrumentAPI.getAllMarketStatus();
      console.log('Successfully fetched market status:', response.data);
      
      // Convert array to object for easier lookup
      const statusMap = {};
      if (response.data && Array.isArray(response.data)) {
        response.data.forEach(item => {
          if (item.symbol) {
            statusMap[item.symbol] = item.status;
          }
        });
      }
      setMarketStatus(statusMap);
    } catch (err) {
      console.error('Error fetching market status:', err);
      
      // Handle specific error cases
      if (err.response?.status === 500) {
        console.warn('Market status service unavailable (500 error) - using default status');
        // Set default status for all instruments
        const defaultStatusMap = {};
        if (Array.isArray(instruments)) {
          instruments.forEach(instrument => {
            defaultStatusMap[instrument.symbol] = 'unknown';
          });
        }
        setMarketStatus(defaultStatusMap);
      } else if (err.response?.status === 404) {
        console.warn('Market status endpoint not found - feature may not be implemented yet');
        setMarketStatus({});
      } else {
        // For other errors, just log and continue with empty status
        console.warn('Market status fetch failed, continuing without status data');
        setMarketStatus({});
      }
    } finally {
      setStatusLoading(false);
    }
  };

  // Combined refresh function
  const handleRefresh = async () => {
    const promises = [fetchInstruments()];
    
    // Only fetch market status if feature is enabled
    if (ENABLE_MARKET_STATUS) {
      promises.push(fetchAllMarketStatus());
    }
    
    await Promise.all(promises);
  };

  // Fetch data automatically on mount
  useEffect(() => {
    console.log('InstrumentManager mounted - fetching data automatically');
    handleRefresh();
    
    // Set up polling for market status only if feature is enabled
    let statusInterval;
    if (ENABLE_MARKET_STATUS) {
      statusInterval = setInterval(fetchAllMarketStatus, 30000);
    }
    
    // Clean up function
    return () => {
      console.log('InstrumentManager unmounting');
      if (statusInterval) {
        clearInterval(statusInterval);
      }
    };
  }, []);

  const filteredInstruments = (Array.isArray(instruments) ? instruments : [])
    .filter(instrument => selectedCategory === 'ALL' || instrument.type.toUpperCase() === selectedCategory)
    .filter(instrument => {
      const query = searchQuery.toLowerCase();
      return (
        instrument.symbol.toLowerCase().includes(query) ||
        instrument.description.toLowerCase().includes(query) ||
        instrument.type.toLowerCase().includes(query)
      );
    });

  // Debug logging
  if (!Array.isArray(instruments)) {
    console.error('Instruments is not an array:', instruments, typeof instruments);
  }

  // Calculate market status statistics
  const marketStats = {
    total: filteredInstruments.length,
    open: filteredInstruments.filter(i => marketStatus[i.symbol] === 'open').length,
    closed: filteredInstruments.filter(i => marketStatus[i.symbol] === 'closed').length,
    holiday: filteredInstruments.filter(i => marketStatus[i.symbol] === 'holiday').length,
    unknown: filteredInstruments.filter(i => !marketStatus[i.symbol] || marketStatus[i.symbol] === 'unknown').length
  };

  // Get set of existing instrument symbols for filtering
  const existingInstrumentSymbols = new Set(
    instruments.map(inst => inst.symbol).filter(Boolean)
  );

  // Filter backup instruments to only show missing ones
  const missingInstruments = backupInstruments.filter(
    inst => !existingInstrumentSymbols.has(inst.symbol)
  );

  // Filter instruments by search term
  const filteredBackupInstruments = missingInstruments.filter(inst =>
    inst.symbol?.toLowerCase().includes(restoreSearchTerm.toLowerCase()) ||
    inst.description?.toLowerCase().includes(restoreSearchTerm.toLowerCase())
  );

  // Handle restore button click
  const handleRestoreClick = () => {
    console.log('🔄 Restore button clicked - creating file input');
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.onchange = async (e) => {
      console.log('📁 File selected:', e.target.files[0]?.name);
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          console.log('📖 File read completed, parsing JSON...');
          try {
            const parsedInstruments = JSON.parse(e.target.result);
            console.log('✅ Parsed JSON successfully:', {
              isArray: Array.isArray(parsedInstruments),
              length: parsedInstruments?.length,
              firstItem: parsedInstruments?.[0]
            });
            
            if (!Array.isArray(parsedInstruments)) {
              throw new Error('Invalid backup file format. Expected an array of instruments.');
            }

            // Store backup instruments and show selection modal
            setBackupInstruments(parsedInstruments);
            setBackupFileName(file.name);
            setSelectedRestoreInstruments([]);
            setRestoreSearchTerm('');
            setShowRestoreModal(true);
          } catch (error) {
            console.error('Error parsing backup file:', error);
            alert('Invalid backup file: ' + (error.message || 'Failed to parse JSON'));
          }
        };
        reader.readAsText(file);
      }
    };
    fileInput.click();
  };

  // Handle selecting/deselecting an instrument
  const handleRestoreInstrumentToggle = (instrument) => {
    setSelectedRestoreInstruments(prev => {
      const exists = prev.find(inst => inst.symbol === instrument.symbol);
      if (exists) {
        return prev.filter(inst => inst.symbol !== instrument.symbol);
      }
      return [...prev, instrument];
    });
  };

  // Handle select all / deselect all
  const handleSelectAllRestore = () => {
    if (selectedRestoreInstruments.length === filteredBackupInstruments.length && 
        filteredBackupInstruments.length > 0) {
      setSelectedRestoreInstruments([]);
    } else {
      setSelectedRestoreInstruments([...filteredBackupInstruments]);
    }
  };

  // Handle confirming the restore
  const handleConfirmRestore = async () => {
    if (selectedRestoreInstruments.length === 0) {
      alert('Please select at least one instrument to restore.');
      return;
    }

    setShowRestoreModal(false);
    await executeRestore(selectedRestoreInstruments);
  };

  // Execute the actual restore operation
  const executeRestore = async (instrumentsToRestore) => {
    console.log('✅ Starting restore operation with selected instruments');
    let successCount = 0;
    let failedInstruments = [];

    try {
      console.log('🚀 Starting restore process...');
      setRestoreProgress({
        isLoading: true,
        current: 0,
        total: instrumentsToRestore.length,
        message: 'Starting restore...'
      });

      for (const [index, instrument] of instrumentsToRestore.entries()) {
        console.log(`🔧 Processing instrument ${index + 1}/${instrumentsToRestore.length}:`, instrument.symbol);
        try {
          setRestoreProgress(prev => ({
            ...prev,
            current: index + 1,
            message: `Restoring ${instrument.symbol || 'Unknown'}...`
          }));

          const normalizedInstrument = {
            symbol: instrument.symbol,
            description: instrument.description,
            type: instrument.type,
            contract_Size: String(instrument.contract_Size || instrument.contractSize || ''),
            min_Order_Size: String(instrument.min_Order_Size || instrument.minOrderSize || ''),
            max_Order_Size: String(instrument.max_Order_Size || instrument.maxOrderSize || ''),
            price_Step: String(instrument.price_Step || instrument.priceStep || ''),
            pips_Point: String(instrument.pips_Point || instrument.pipsPoint || ''),
            spread_pipettes: String(instrument.spread_pipettes || instrument.spreadPipettes || '0'),
            decimals: String(instrument.decimals || ''),
            order_Quantity_Step: String(instrument.order_Quantity_Step || instrument.orderQuantityStep || ''),
            endpoint: instrument.endpoint,
            path: Array.isArray(instrument.path) 
              ? instrument.path 
              : typeof instrument.path === 'string' 
                ? instrument.path.split(',').map(p => p.trim()).filter(p => p !== '')
                : []
          };

          console.log('📋 Normalized instrument:', normalizedInstrument);

          const missingFields = Object.entries(normalizedInstrument)
            .filter(([key, value]) => {
              if (key === 'spread_pipettes' && value === '0') return false;
              return !value || value === 'undefined' || value === 'null' || value === '';
            })
            .map(([key]) => key);

          if (missingFields.length > 0) {
            console.log('⚠️ Missing fields detected:', missingFields);
            failedInstruments.push({
              symbol: instrument.symbol || 'Unknown',
              reason: `Missing required fields: ${missingFields.join(', ')}`
            });
            continue;
          }

          delete normalizedInstrument._id;
          
          console.log('📡 Calling instrumentAPI.create...');
          const createResponse = await instrumentAPI.create(normalizedInstrument);
          console.log('✅ API response:', createResponse);
          
          await new Promise(resolve => setTimeout(resolve, 100));
          successCount++;
        } catch (error) {
          console.error('❌ Error creating instrument:', error);
          failedInstruments.push({
            symbol: instrument.symbol || 'Unknown',
            reason: error.message || 'API error'
          });
        }
      }

      console.log(`🎉 Restore completed. Success: ${successCount}, Failed: ${failedInstruments.length}`);

      setTimeout(() => {
        if (failedInstruments.length > 0) {
          const failureMessage = failedInstruments
            .map(f => `${f.symbol}: ${f.reason}`)
            .join('\n');
          alert(
            `Restore completed with some issues:\n` +
            `Successfully restored: ${successCount} instruments\n` +
            `Failed: ${failedInstruments.length} instruments\n\n` +
            `Failed instruments:\n${failureMessage}`
          );
        } else {
          alert(`Successfully restored all ${successCount} instruments!`);
        }

        setRestoreProgress({
          isLoading: false,
          current: 0,
          total: 0,
          message: ''
        });
        handleRefresh();
      }, 500);

    } catch (error) {
      console.error('Error during restore:', error);
      alert('Failed to restore instruments: ' + (error.message || 'Unknown error'));
      setRestoreProgress({
        isLoading: false,
        current: 0,
        total: 0,
        message: ''
      });
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false });
  };

  // Debug function to track add instrument button clicks
  const handleAddInstrumentClick = (source) => {
    console.log(`🔧 Add Instrument clicked from: ${source}`);
    console.log(`📊 Current showAddModal state: ${showAddModal}`);
    setShowAddModal(true);
    console.log(`✅ showAddModal set to true`);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h1 className="section-title">Instrument Manager</h1>
        <p className="section-subtitle">
          Manage financial instruments{ENABLE_TRADING_HOURS_PRESETS ? ', trading hours,' : ''}{ENABLE_MARKET_STATUS ? ' and monitor real-time market status' : ''}
        </p>
      </div>
      
      {/* Market Status Statistics */}
      {ENABLE_MARKET_STATUS && (
        <div className="instrument-stats">
          <div className="instrument-stat-card stat-total">
            <div className="instrument-stat-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="instrument-stat-content">
              <h4>Total Instruments</h4>
              <div className="instrument-stat-value">{marketStats.total}</div>
            </div>
          </div>
          
          <div className="instrument-stat-card stat-open">
            <div className="instrument-stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="instrument-stat-content">
              <h4>Markets Open</h4>
              <div className="instrument-stat-value">{marketStats.open}</div>
            </div>
          </div>
          
          <div className="instrument-stat-card stat-closed">
            <div className="instrument-stat-icon">
              <i className="fas fa-pause-circle"></i>
            </div>
            <div className="instrument-stat-content">
              <h4>Markets Closed</h4>
              <div className="instrument-stat-value">{marketStats.closed}</div>
            </div>
          </div>
          
          <div className="instrument-stat-card stat-holiday">
            <div className="instrument-stat-icon">
              <i className="fas fa-calendar-times"></i>
            </div>
            <div className="instrument-stat-content">
              <h4>Holiday/Unknown</h4>
              <div className="instrument-stat-value">{marketStats.holiday + marketStats.unknown}</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="action-bar">
        <div className="action-bar-left">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-input-icon"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Search instruments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search instruments"
            />
          </div>
          
          <select
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            aria-label="Filter by category"
          >
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
        
        <div className="action-bar-right">
          <button 
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={loading || statusLoading}
            aria-label="Refresh data"
          >
            <i className={`fas fa-sync-alt ${loading || statusLoading ? 'fa-spin' : ''}`}></i>
            {loading ? 'Loading...' : statusLoading ? 'Updating...' : 'Refresh'}
          </button>
          
          <button 
            className="btn-secondary"
            onClick={handleRestoreClick}
            aria-label="Restore instruments from backup file"
          >
            <i className="fas fa-upload"></i>
            Restore
          </button>
          
          {ENABLE_TRADING_HOURS_PRESETS && (
            <>
              <span className="action-bar-divider"></span>
              <button 
                className="btn-secondary"
                onClick={() => setShowBulkPresetModal(true)}
                disabled={filteredInstruments.length === 0}
                aria-label="Apply preset to multiple instruments"
              >
                <i className="fas fa-magic"></i>
                Bulk Preset
              </button>
              
              <button 
                className="btn-secondary"
                onClick={() => setShowPresetManager(true)}
                aria-label="Manage trading hours presets"
              >
                <i className="fas fa-cogs"></i>
                Presets
              </button>
            </>
          )}
          
          <span className="action-bar-divider"></span>
          <button 
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
            aria-label="Add new instrument"
          >
            <i className="fas fa-plus"></i>
            Add Instrument
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-banner">
          <div className="error-content">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="error-close">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      {loading && !dataFetched && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading instruments...</p>
        </div>
      )}
      
      {dataFetched && !loading && filteredInstruments.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <i className="fas fa-search"></i>
          </div>
          <h3>No Instruments Found</h3>
          <p>
            {searchQuery || selectedCategory !== 'ALL' 
              ? 'Try adjusting your search criteria'
              : 'Start by adding your first instrument'
            }
          </p>
          {(!searchQuery && selectedCategory === 'ALL') && (
            <button onClick={() => setShowAddModal(true)} className="btn-primary">
              <i className="fas fa-plus"></i>
              Add First Instrument
            </button>
          )}
        </div>
      )}
      
      {dataFetched && !loading && filteredInstruments.length > 0 && (
        <InstrumentList 
          instruments={filteredInstruments} 
          marketStatus={marketStatus}
          onInstrumentUpdated={handleRefresh}
          validateSpreadPipettes={validateSpreadPipettes}
        />
      )}
      
      {showAddModal && (
        <AddInstrument 
          onInstrumentAdded={handleRefresh} 
          validateSpreadPipettes={validateSpreadPipettes}
          onClose={() => setShowAddModal(false)}
          isModal={true}
        />
      )}
      
      {showPresetManager && ENABLE_TRADING_HOURS_PRESETS && (
        <PresetManager 
          onClose={() => setShowPresetManager(false)}
        />
      )}
      
      {showBulkPresetModal && ENABLE_TRADING_HOURS_PRESETS && (
        <BulkPresetModal 
          instruments={filteredInstruments}
          onClose={() => setShowBulkPresetModal(false)}
          onSave={handleRefresh}
          onOpenPresetManager={() => setShowPresetManager(true)}
        />
      )}
      
      {confirmDialog.isOpen && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={closeConfirmDialog}
        />
      )}
      
      {restoreProgress.isLoading && (
        <div className="restore-progress">
          <div className="restore-progress-bar">
            <div className="restore-progress-fill" style={{ width: `${(restoreProgress.current / restoreProgress.total) * 100}%` }}></div>
          </div>
          <p>{restoreProgress.message}</p>
        </div>
      )}
      
      {/* Restore Selection Modal */}
      {showRestoreModal && (
        <div className="modal-overlay" onClick={() => setShowRestoreModal(false)}>
          <div className="modal-content restore-selection-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Instruments to Restore</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowRestoreModal(false)}
                disabled={restoreProgress.isLoading}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="restore-modal-info">
              <p className="backup-file-name">
                <i className="fas fa-file-alt"></i>
                Backup file: <strong>{backupFileName}</strong>
              </p>
              <p className="restore-hint">
                Showing instruments that are NOT already in the system.
                Select the instruments you want to restore.
              </p>
            </div>

            {/* Search and Selection Bar */}
            <div className="restore-search-bar">
              <div className="search-input-wrapper">
                <i className="fas fa-search search-input-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search instruments..."
                  value={restoreSearchTerm}
                  onChange={(e) => setRestoreSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="restore-selection-info">
                <span className="selected-count">
                  {selectedRestoreInstruments.length} of {filteredBackupInstruments.length} selected
                </span>
                <button
                  className="btn-text"
                  onClick={handleSelectAllRestore}
                  disabled={filteredBackupInstruments.length === 0}
                >
                  {selectedRestoreInstruments.length === filteredBackupInstruments.length && filteredBackupInstruments.length > 0
                    ? 'Deselect All'
                    : 'Select All'
                  }
                </button>
              </div>
            </div>

            {/* Instruments List */}
            <div className="restore-instruments-list">
              {filteredBackupInstruments.length > 0 ? (
                filteredBackupInstruments.map((instrument) => (
                  <div 
                    key={instrument.symbol} 
                    className={`restore-instrument-item ${selectedRestoreInstruments.find(inst => inst.symbol === instrument.symbol) ? 'selected' : ''}`}
                    onClick={() => handleRestoreInstrumentToggle(instrument)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRestoreInstruments.some(inst => inst.symbol === instrument.symbol)}
                      onChange={() => handleRestoreInstrumentToggle(instrument)}
                      className="restore-instrument-checkbox"
                    />
                    <div className="restore-instrument-info">
                      <span className="restore-instrument-symbol">{instrument.symbol}</span>
                      <span className="restore-instrument-description">
                        {instrument.description || 'No description'}
                      </span>
                      <span className="restore-instrument-type">{instrument.type}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-instruments-message">
                  <i className="fas fa-check-circle"></i>
                  <h3>All instruments already exist</h3>
                  <p>
                    {restoreSearchTerm 
                      ? 'No instruments match your search criteria.'
                      : 'All instruments from this backup file are already in the system.'}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowRestoreModal(false)}
                disabled={restoreProgress.isLoading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleConfirmRestore}
                disabled={selectedRestoreInstruments.length === 0 || restoreProgress.isLoading}
              >
                <i className="fas fa-upload"></i>
                Restore {selectedRestoreInstruments.length} Instrument{selectedRestoreInstruments.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstrumentManager; 